import { Component } from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { getGoogleDriveUrl, googleDriveFallbackName, isGoogleDriveSource } from "src/files/googleDriveUrl";
import { localize } from "src/l10n";
import { fetchAttachedFolder } from "src/pages/page-actions";
import api from "src/spintr/SpintrApi";
import { IApplicationState } from "src/spintr/reducer";
import { Label, Loader, UnstyledButton } from "src/ui";
import { decodeHtmlEntities } from "src/utils";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import TinyFile from "./TinyFile";
import "./TinyFolder.scss";

interface IProps {
    item: any;
    minimizeAttachedFilelists?: boolean;
    dispatch: (action: Action) => void;
    attachedFolder: any;
}

interface IState {
    isExpanded: boolean;
    isLoading?: boolean;
    files?: any;
    isError?: boolean;
}

class TinyFolder extends Component<IProps, IState> {
    // Google Drive folders are linked directly instead of listed: the
    // drive.file scope can't list folders the user hasn't opened through
    // the app.
    private readonly isGoogleDriveFolder: boolean;

    constructor(props) {
        super(props);

        this.isGoogleDriveFolder = !!props.item.externalId && isGoogleDriveSource(props.item.source);

        this.state = {
            isExpanded: !props.minimizeAttachedFilelists,
            isLoading: !!props.item.externalId && !this.isGoogleDriveFolder,
        };

        if (props.item.externalId) {
            if (!this.isGoogleDriveFolder) {
                this.fetchExternalFolder();
            }
        } else if (!this.props.attachedFolder) {
            this.props.dispatch(
                fetchAttachedFolder(
                    this.props.item,
                    this.props.item.source,
                )
            );
        }
    }

    private fetchExternalFolder = async () => {
        try {
            const response = await api.get("/api/v1/files", {
                params: {
                    folderId: this.props.item.externalId,
                    source: this.props.item.source,
                    includeThumbnail: false,
                },
            });

            this.setState({
                isLoading: false,
                files: response.data.items.filter((file) => !file.isDirectory),
            });
        } catch {
            this.setState({
                isLoading: false,
                isError: true,
            });
        }
    };

    public render() {
        const { attachedFolder } = this.props

        if (this.isGoogleDriveFolder) {
            const folderName = decodeHtmlEntities(this.props.item.name || googleDriveFallbackName);

            return (
                <div className="TinyFolder-wrapper">
                    <a
                        href={getGoogleDriveUrl(this.props.item.externalId)}
                        target="_blank"
                        rel="noreferrer"
                        title={folderName}
                    >
                        <div className="TinyFolder-header no-user-select">
                            <Visage2Icon className="TinyFolder-header-icon" icon="folder" />
                            <Label className="TinyFolder-header-text" weight="medium">
                                {folderName}
                            </Label>
                        </div>
                    </a>
                </div>
            );
        }

        if (this.state.isLoading || attachedFolder?.isLoading ) {
            return <Loader />;
        }

        if (this.state.isError || attachedFolder?.isError) {
            return <Label className="TinyFolder-MissingRights">{localize("YOU_LACK_FILE_RIGHTS")}</Label>;
        }

        let files = attachedFolder?.files || attachedFolder?.items || this.state.files || [];
        files = files?.filter(f => !f.isDirectory)?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

        const folderName = this.props.attachedFolder?.name || this.props.item.name;
        
        return (
            <div className="TinyFolder-wrapper">
                <UnstyledButton onClick={() => {
                    this.setState({
                        isExpanded: !this.state.isExpanded
                    });
                }}>
                    <div className="TinyFolder-header no-user-select">
                        <Visage2Icon className="TinyFolder-header-icon" icon={this.state.isExpanded ? "minus" : "add"} />
                        <Label className="TinyFolder-header-text" weight="medium">
                            {
                                decodeHtmlEntities(folderName)
                            }
                        </Label>
                    </div>
                </UnstyledButton>
                {this.state.isExpanded &&
                    (files.length === 0 ? (
                        <Label>{localize("IngaFilerHittades")}</Label>
                    ) : (
                        <div className="TinyFolder-content">
                            {files.map((f, idx) => (
                                // sharepoint list searches don't have ids.. using index instead, bad?
                                <TinyFile
                                    key={idx}
                                    item={f}
                                    external={this.props.item.external}
                                />
                            ))}
                        </div>
                    ))}
            </div>
        );
    }
}

const mapStateToProps = (state: IApplicationState, props) => {
    return {
        ...props,
        minimizeAttachedFilelists: state.instance.get("minimizeAttachedFilelists") as boolean,
        attachedFolder: state.pages.attachedFolders.find((f) => f.id == props.item.id)
    };
};

export default connect(mapStateToProps)(TinyFolder);